import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { CustomerProvider } from './contexts/CustomerContext';
import { LocationProvider } from './contexts/LocationContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout/Layout';
import LoadingSpinner from './components/Common/LoadingSpinner';
import ConnectionBanner from './components/Common/ConnectionBanner';
import DevToolsErrorBoundary from './components/DevToolsErrorBoundary';
import DevToolsInitializer from './components/DevToolsInitializer';

// Import developer tools services
import devToolsService from './services/DevToolsService';
import remoteDebugService from './services/RemoteDebugService';

// Auth pages (direct load)
import Login from './pages/Login';
import Signup from './pages/Signup';

// Payment pages (public - accessible via invoice links)
import PayInvoice from './pages/PayInvoice';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentCancelled from './pages/PaymentCancelled';
import Storefront from './pages/Storefront';
import BusinessPage from './pages/BusinessPage';
import BusinessLegalPage from './pages/BusinessLegalPage';



// Public pages
const Portal = lazy(() => import('./pages/Portal'));
// Change order sent for the customer's approval (token link, no login; 2026-10-22)
const ChangeOrderApproval = lazy(() => import('./pages/ChangeOrderApproval'));

// Main pages (lazy loaded for better performance)
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Messages = lazy(() => import('./pages/Messages'));
const MyRequests = lazy(() => import('./pages/MyRequests'));
const RequestDetail = lazy(() => import('./pages/RequestDetail'));
const Browse = lazy(() => import('./pages/Browse'));
const Booking = lazy(() => import('./pages/Booking'));
const Profile = lazy(() => import('./pages/Profile'));
const Jobs = lazy(() => import('./pages/Jobs'));
const Invoices = lazy(() => import('./pages/Invoices'));
const Quotes = lazy(() => import('./pages/Quotes'));

// Initialize developer tools and debugging services
const initializeDevTools = async (supabaseClient = null) => {
  try {
    // Initialize DevTools Service with supabase client
    await devToolsService.initialize(supabaseClient);

    // Initialize Remote Debug Service if WebSocket URL is provided
    const debugWsUrl = process.env.REACT_APP_DEBUG_WS_URL;
    if (debugWsUrl) {
      try {
        await remoteDebugService.initialize({
          url: debugWsUrl,
          autoReconnect: true
        });
        console.log('🔗 Remote Debug Service connected');
      } catch (error) {
        console.log('🟡 Remote Debug Service connection failed (this is normal if no debug server is running)');
      }
    } else {
      console.log('🟡 Remote Debug Service disabled (set REACT_APP_DEBUG_WS_URL to enable)');
    }

    console.log('🛠️ Customer Portal Developer tools initialized successfully');
  } catch (error) {
    console.warn('⚠️ Developer tools initialization failed:', error);
  }
};

function App() {
  useEffect(() => {
    // Initialize developer tools on app start (without supabase client initially)
    if (process.env.NODE_ENV === 'development' && !window.__DEVTOOLS_INITIALIZED__) {
      window.__DEVTOOLS_INITIALIZED__ = true;
      initializeDevTools();
    }

    // Make devLogger globally available for error boundary
    if (window.devLogger) {
      window.devLogger.addLog('INFO', 'Customer Portal application started', 'app');
    }

    // Enable dark mode by default for glassmorphism design
    document.documentElement.classList.add('dark');
  }, []);
  return (
    <DevToolsErrorBoundary componentName="App">
      <CustomerProvider>
        <LocationProvider>
          <DevToolsInitializer />
          <Router>
          <ConnectionBanner />
          <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            <Suspense fallback={<LoadingSpinner />}>
              <Routes>
              {/* Public routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/public/quote/:token" element={<Portal />} />
              <Route path="/public/change-order/:token" element={<ChangeOrderApproval />} />
              <Route path="/portal" element={<Portal />} />

              {/* Public payment page - no authentication required */}
              <Route path="/payment/:token" element={<PayInvoice />} />
              <Route path="/payment/success" element={<PaymentSuccess />} />
              {/* Stripe's cancel_url — see supabase/functions/stripe-create-checkout/index.ts */}
              <Route path="/payment/cancel" element={<PaymentCancelled />} />

              {/* Public storefront - per-company product catalog */}
              <Route path="/store/:companyId" element={<Storefront />} />

              {/* Public business page - the contractor's own public page, used
                  as their "business website" for toll-free verification. Must
                  stay public and unauthenticated: Twilio rejects a URL that
                  requires a login (30473). */}
              <Route path="/business/:companyId" element={<BusinessPage />} />
              {/* The business's own privacy policy and text terms (#6069) - the
                  URLs its SMS registration needs. Public for the same reason. */}
              <Route path="/business/:companyId/privacy" element={<BusinessLegalPage kind="privacy" />} />
              <Route path="/business/:companyId/terms" element={<BusinessLegalPage kind="terms" />} />

              {/* Protected routes with layout */}
              <Route path="/" element={
                <ProtectedRoute>
                  <Layout>
                    <Dashboard />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <Layout>
                    <Dashboard />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/requests" element={
                <ProtectedRoute>
                  <Layout>
                    <MyRequests />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/requests/:requestId" element={
                <ProtectedRoute>
                  <Layout>
                    <RequestDetail />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/browse" element={
                <ProtectedRoute>
                  <Layout>
                    <Browse />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/booking" element={
                <ProtectedRoute>
                  <Layout>
                    <Booking />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/messages" element={
                <ProtectedRoute>
                  <Layout>
                    <Messages />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/profile" element={
                <ProtectedRoute>
                  <Layout>
                    <Profile />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/jobs" element={
                <ProtectedRoute>
                  <Layout>
                    <Jobs />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/quotes" element={
                <ProtectedRoute>
                  <Layout>
                    <Quotes />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/invoices" element={
                <ProtectedRoute>
                  <Layout>
                    <Invoices />
                  </Layout>
                </ProtectedRoute>
              } />


            </Routes>
          </Suspense>
        </div>
      </Router>
    </LocationProvider>
    </CustomerProvider>
    </DevToolsErrorBoundary>
  );
}

export default App;
